import type { IsoDate, IsoDateTime, Money } from "../primitives";
import { STORE_TIMEZONE } from "../filters/timezone";
import type { FinancialStatus } from "../types/orders";
import {
  bucketStart,
  bucketStarts,
  daysBetween,
  fromPaise,
  nextBucket,
  shiftDate,
  storeDate,
  toPaise,
  type OrderSeriesBucket,
} from "./series";

/**
 * The Sales report (#486): orders over a range of the store's days, as a chart
 * and two breakdowns, and the arithmetic both data sources share to produce it.
 *
 * The API counts in Postgres and the fixture adapter counts in memory, and the
 * two have to agree to the last figure, as they do for the orders chart
 * (`series.ts`). So what can be shared is shared: which bucket a range is drawn
 * in, the zero-filled bucket list, the order the breakdowns are ranked in, and
 * decimal-exact money sums live here. Each side supplies only the grouped rows.
 *
 * No order is in a `SalesSummary` — counts and sums only — so a year of orders
 * is a few kilobytes and no customer's details leave the database to be added
 * up in a browser.
 */

/** The report opens on this many days, ending today, when no dates are given. */
export const SALES_DEFAULT_DAYS = 60;

/** Up to this many days the chart has a bar per day. */
export const SALES_DAILY_DAYS = 92;

/** Up to this many days a bar per week; past it, a bar per month. */
export const SALES_WEEKLY_DAYS = 366;

/**
 * What a range is drawn in: a bar per day while that is at most a screenful,
 * then per week, then per month.
 *
 * Chosen from the range alone, so a link to a range draws the same bars for
 * everyone and the server and the fixture cannot disagree. Ninety-two days is
 * a bar per day at about six pixels wide on a desktop card; a year of days is
 * a comb nobody can read, and ten years of weeks is the same comb again.
 */
export function salesBucket(from: IsoDate, to: IsoDate): OrderSeriesBucket {
  const days = daysBetween(from, to) + 1;
  if (days <= SALES_DAILY_DAYS) return "day";
  if (days <= SALES_WEEKLY_DAYS) return "week";
  return "month";
}

/**
 * The days of a bucket that a range actually counts.
 *
 * A week or a month at either end of a range is drawn as one whole bar and
 * counts only the days inside the range: for 6 May to 5 Oct the first bar is
 * "the week of Monday 4 May" and holds 6 to 10 May. Whoever names the bar has
 * to say which days it holds, and this is where they are worked out, once, for
 * the tooltip and the axis.
 *
 * `start` is the bucket's first day, as `SalesSummary.buckets` has it.
 */
export function salesBucketSpan(
  start: IsoDate,
  bucket: OrderSeriesBucket,
  range: SalesSummaryQuery,
): { from: IsoDate; to: IsoDate; partial: boolean } {
  const end = shiftDate(nextBucket(start, bucket), -1);
  const from = start < range.from ? range.from : start;
  const to = end > range.to ? range.to : end;
  return { from, to, partial: from !== start || to !== end };
}

/** What the report asks for. `from` and `to` are the store's calendar dates. */
export interface SalesSummaryQuery {
  /** First day counted, inclusive, in the store's timezone. */
  from: IsoDate;
  /** Last day counted, inclusive, in the store's timezone. */
  to: IsoDate;
  /**
   * Count cancelled orders too. Absent means no: Reports > Sales leaves them
   * out unless the switch is on, as Reports > Orders does (#506).
   */
  includeCancelled?: boolean;
}

/** One bar: a bucket's orders and what they add up to. */
export interface SalesBucketFigures {
  /** The bucket's first day — itself, its Monday, or the 1st. */
  date: IsoDate;
  orders: number;
  revenue: Money;
}

/** What a payment status reads as when an order carries none the domain names. */
export type SalesPaymentStatus = FinancialStatus | "unknown";

/** Orders and revenue for one shipping state. */
export interface SalesStateFigures {
  /** The name as the channel gave it — `province`, not the two-letter code. */
  state: string;
  orders: number;
  revenue: Money;
}

/** Orders and revenue for one payment status. */
export interface SalesStatusFigures {
  status: SalesPaymentStatus;
  orders: number;
  revenue: Money;
}

/**
 * The Sales report's data for a range.
 *
 * Every figure counts the same orders: those placed on a day in the range, in
 * the store's timezone. The chart, the two tables and the totals are one
 * query's answer, so they add up to each other.
 */
export interface SalesSummary {
  from: IsoDate;
  to: IsoDate;
  /** What `buckets` are: see `salesBucket`. */
  bucket: OrderSeriesBucket;
  /** The zone the days were cut in. */
  timezone: string;
  /**
   * Every bucket in the range, in order, zero-filled — a day with no orders is
   * a bar at zero, not a gap. A week starts on Monday and a month on the 1st,
   * so the first and last bucket may begin before `from` or run past `to`;
   * only orders inside the range count.
   */
  buckets: SalesBucketFigures[];
  /** Largest revenue first. */
  byState: SalesStateFigures[];
  /** Largest revenue first. */
  byPaymentStatus: SalesStatusFigures[];
  totals: { orders: number; revenue: Money };
}

/** What one group of a grouped query holds. */
interface GroupRow {
  orders: number;
  revenue: Money;
}

/** The grouped rows a data source supplies; `assembleSalesSummary` does the rest. */
export interface SalesRows {
  /** `date` is the bucket's first day, as `salesBucket` says buckets are cut. */
  buckets: (GroupRow & { date: IsoDate })[];
  states: (GroupRow & { state: string })[];
  /** `status` is the order's financial status, or `null` where it has none. */
  statuses: (GroupRow & { status: string | null })[];
}

/** The name an order with no shipping province is reported under. */
export const UNKNOWN_STATE = "Unknown";

/**
 * The shipping state a report files an order under: the province's name as the
 * channel wrote it, trimmed, or `Unknown` for an order that has none.
 *
 * `province`, not `provinceCode`: a report someone reads is for a human, and
 * "Maharashtra" is what they are looking for, not "MH". The API says the same
 * in SQL (`routes/sales-summary.ts`).
 */
export function salesStateOf(province: string | null | undefined): string {
  const trimmed = province?.trim();
  return trimmed ? trimmed : UNKNOWN_STATE;
}

const FINANCIAL_STATUSES: readonly string[] = [
  "pending",
  "authorized",
  "partially_paid",
  "paid",
  "partially_refunded",
  "refunded",
  "voided",
] satisfies FinancialStatus[];

/**
 * An order's payment status as the report names it: the domain's own word, or
 * `unknown` for an order with none. A word the domain does not name is
 * `unknown` too, so a status Shopify adds one day is a row in the table rather
 * than a label nothing can print.
 */
export function salesStatusOf(status: string | null): SalesPaymentStatus {
  return status !== null && FINANCIAL_STATUSES.includes(status)
    ? (status as FinancialStatus)
    : "unknown";
}

interface Tally {
  orders: number;
  paise: bigint;
}

function tally(): Tally {
  return { orders: 0, paise: 0n };
}

function addTo(target: Tally, row: GroupRow) {
  target.orders += row.orders;
  target.paise += toPaise(row.revenue);
}

/**
 * Largest revenue first; ties to the one with more orders, then by name.
 *
 * A total order, so the table reads the same whichever way the rows arrived:
 * Postgres returns groups in whatever order its plan produced them, and the
 * fixture in the order it walked its orders.
 */
function ranked<T extends string>(
  groups: Map<T, Tally>,
): { key: T; orders: number; revenue: Money }[] {
  return [...groups.entries()]
    .sort(([keyA, a], [keyB, b]) => {
      if (a.paise !== b.paise) return a.paise > b.paise ? -1 : 1;
      if (a.orders !== b.orders) return b.orders - a.orders;
      return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    })
    .map(([key, group]) => ({
      key,
      orders: group.orders,
      revenue: fromPaise(group.paise),
    }));
}

function grouped<T extends string>(
  rows: readonly (GroupRow & { key: T })[],
): Map<T, Tally> {
  const groups = new Map<T, Tally>();
  for (const row of rows) {
    const group = groups.get(row.key) ?? tally();
    addTo(group, row);
    groups.set(row.key, group);
  }
  return groups;
}

/**
 * The report from grouped rows.
 *
 * Zero-fills the buckets, ranks the two breakdowns and adds up the totals.
 * Throws on a bucket row that is not in the range's bucket list: that would
 * mean the two sides disagree about where a week starts, and dropping the row
 * quietly is how a chart ends up wrong.
 */
export function assembleSalesSummary(
  query: SalesSummaryQuery,
  rows: SalesRows,
  timezone: string = STORE_TIMEZONE,
): SalesSummary {
  const bucket = salesBucket(query.from, query.to);
  const starts = bucketStarts(query.from, query.to, bucket);
  const position = new Map(starts.map((start, index) => [start, index]));
  const bars = starts.map(() => tally());
  const everything = tally();

  for (const row of rows.buckets) {
    const index = position.get(row.date);
    const bar = index === undefined ? undefined : bars[index];
    if (!bar) {
      throw new Error(
        `Sales row for ${row.date} is outside ${query.from}..${query.to}`,
      );
    }
    addTo(bar, row);
    addTo(everything, row);
  }

  return {
    from: query.from,
    to: query.to,
    bucket,
    timezone,
    buckets: starts.map((date, index) => ({
      date,
      orders: bars[index]?.orders ?? 0,
      revenue: fromPaise(bars[index]?.paise ?? 0n),
    })),
    byState: ranked(
      grouped(rows.states.map((row) => ({ ...row, key: row.state }))),
    ).map(({ key, orders, revenue }) => ({ state: key, orders, revenue })),
    byPaymentStatus: ranked(
      grouped(
        rows.statuses.map((row) => ({
          ...row,
          key: salesStatusOf(row.status),
        })),
      ),
    ).map(({ key, orders, revenue }) => ({ status: key, orders, revenue })),
    totals: { orders: everything.orders, revenue: fromPaise(everything.paise) },
  };
}

/** One order, reduced to what the report counts. For the fixture adapter. */
export interface SalesPoint {
  placedAt: IsoDateTime;
  total: Money;
  /** The shipping province's name, or null. */
  province: string | null;
  /** The order's financial status, or null. */
  status: string | null;
}

/**
 * The grouped rows, from orders in memory — what Postgres' `group by` returns
 * on the API side.
 *
 * The caller has already applied the range; this cuts each order's day in the
 * store's zone, buckets it and groups.
 */
export function salesRowsFromPoints(
  points: readonly SalesPoint[],
  bucket: OrderSeriesBucket,
  timezone: string = STORE_TIMEZONE,
): SalesRows {
  const buckets = new Map<IsoDate, Tally>();
  const states = new Map<string, Tally>();
  const statuses = new Map<string, Tally>();
  const none = "\u0000none";

  for (const point of points) {
    const row = { orders: 1, revenue: point.total };
    const start = bucketStart(storeDate(point.placedAt, timezone), bucket);
    for (const [groups, key] of [
      [buckets, start],
      [states, salesStateOf(point.province)],
      [statuses, point.status ?? none],
    ] as const) {
      const group = groups.get(key) ?? tally();
      addTo(group, row);
      groups.set(key, group);
    }
  }

  const rowOf = (group: Tally) => ({
    orders: group.orders,
    revenue: fromPaise(group.paise),
  });
  return {
    buckets: [...buckets].map(([date, group]) => ({ date, ...rowOf(group) })),
    states: [...states].map(([state, group]) => ({ state, ...rowOf(group) })),
    statuses: [...statuses].map(([status, group]) => ({
      status: status === none ? null : status,
      ...rowOf(group),
    })),
  };
}
